import { EPlatform } from '@/utils/enums/platform-enum';

/** 服务端 Script 与客户端兜底共享的 Sensors 节点 ID。 */
export const SENSORS_SDK_SCRIPT_ID = 'zeelool-sensors-sdk';

/** 当前生产使用的 Sensors JavaScript SDK 固定版本地址。 */
export const SENSORS_SDK_URL =
  'https://common.xmslol.com/libs/sensorsdata@1.26.18/web/sensorsdata.js';

/** 单页面生命周期内复用的 Sensors 兜底加载 Promise。 */
let sensorsSdkPromise: Promise<void> | null = null;

/** 单页面生命周期内复用的 Snowplow browser tracker Promise。 */
let browserTrackerPromise: Promise<typeof import('@snowplow/browser-tracker')> | null = null;

/** 单页面生命周期内复用的 Snowplow webview tracker Promise。 */
let webviewTrackerPromise: Promise<typeof import('@snowplow/webview-tracker')> | null = null;

/** 加载 Sensors SDK；SSR Script 已执行时直接完成，否则复用已有节点或创建唯一兜底节点。 */
export function loadSensorsSdk(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.sensorsDataAnalytic201505) return Promise.resolve();
  if (sensorsSdkPromise) return sensorsSdkPromise;

  const existingScript = document.getElementById(SENSORS_SDK_SCRIPT_ID);
  const script = existingScript
    ? (existingScript as HTMLScriptElement)
    : document.createElement('script');

  const loadPromise = new Promise<void>((resolve, reject) => {
    script.onload = () => {
      if (window.sensorsDataAnalytic201505) {
        resolve();
        return;
      }
      reject(new Error('Sensors SDK global is unavailable after script load'));
    };
    script.onerror = () => reject(new Error('Failed to load Sensors Analytics script'));

    if (!existingScript) {
      script.id = SENSORS_SDK_SCRIPT_ID;
      script.async = true;
      script.fetchPriority = 'high';
      script.src = SENSORS_SDK_URL;
      document.head.appendChild(script);
    }
  });

  // 自建节点失败后同时移除失效 DOM，确保显式重试会重新发起下载；SSR 节点保持不变。
  sensorsSdkPromise = loadPromise.catch((error: unknown) => {
    if (!existingScript) script.remove();
    sensorsSdkPromise = null;
    throw error;
  });
  return sensorsSdkPromise;
}

/** 返回缓存式 Snowplow browser tracker 模块 Promise。 */
export function loadSnowplowBrowserTracker(): Promise<typeof import('@snowplow/browser-tracker')> {
  browserTrackerPromise ??= import('@snowplow/browser-tracker');
  return browserTrackerPromise;
}

/** 返回缓存式 Snowplow webview tracker 模块 Promise。 */
export function loadSnowplowWebviewTracker(): Promise<typeof import('@snowplow/webview-tracker')> {
  webviewTrackerPromise ??= import('@snowplow/webview-tracker');
  return webviewTrackerPromise;
}

/** 按运行平台提前启动正确的 Snowplow Tracker 模块下载。 */
export function preloadSnowplowTracker(deviceType: EPlatform): Promise<unknown> {
  return [EPlatform.IOS, EPlatform.ANDROID].includes(deviceType)
    ? loadSnowplowWebviewTracker()
    : loadSnowplowBrowserTracker();
}
